import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Database, Loader2, CheckCircle, AlertCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { FunctionsHttpError } from '@supabase/supabase-js';
import { RentCastPropertyData, RentCastApiResponse } from "@/types/rentcast";

interface ImportPropertyDataButtonProps {
  address: string;
  city: string;
  state: string;
  zipCode: string;
  onDataImported: (data: RentCastPropertyData) => void;
  disabled?: boolean;
}

type ImportState = 'idle' | 'loading' | 'success' | 'error';

export const ImportPropertyDataButton = ({
  address,
  city,
  state,
  zipCode,
  onDataImported,
  disabled = false
}: ImportPropertyDataButtonProps) => {
  const [importState, setImportState] = useState<ImportState>('idle');
  const [errorMessage, setErrorMessage] = useState<string>('');

  const handleImport = async () => {
    setImportState('loading');
    setErrorMessage('');

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('You must be logged in to import property data');
      }

      const { data, error } = await supabase.functions.invoke<RentCastApiResponse>(
        'get-rentcast-property-details',
        {
          body: { address, city, state, zipCode },
          headers: {
            Authorization: `Bearer ${session.access_token}`
          }
        }
      );

      if (error) {
        console.error('Edge function error:', error);
        
        // Extract user-friendly error from edge function response body
        let userFriendlyError = 'Failed to fetch property details';
        
        if (error instanceof FunctionsHttpError) {
          try {
            const errorBody = await error.context.json();
            if (errorBody?.error) {
              userFriendlyError = errorBody.error;
            }
          } catch (parseError) {
            console.error('Could not parse error response:', parseError);
          }
        }
        
        // Map common "not found" errors to more helpful messages
        if (userFriendlyError === 'Property details not found for this address' || 
            userFriendlyError === 'No property data found for this address') {
          throw new Error('No public records found for this address. This could mean the property is new, recently built, or the address format doesn\'t match public records. You can still enter the property details manually.');
        }
        
        throw new Error(userFriendlyError);
      }

      if (!data?.success) {
        throw new Error(data?.error || 'Failed to fetch property details');
      }

      if (data.data) {
        setImportState('success');
        onDataImported(data.data);
        
        // Reset to idle after 3 seconds
        setTimeout(() => {
          setImportState('idle');
        }, 3000);
      } else {
        throw new Error('No property data returned');
      }
    } catch (error) {
      console.error('Import error:', error);
      setImportState('error');
      setErrorMessage(error instanceof Error ? error.message : 'An unexpected error occurred');
    }
  };

  const isDisabled = disabled || !address || !city || !state || !zipCode || importState === 'loading';

  return (
    <div className="space-y-3">
      <Alert className="bg-muted border-border">
        <Database className="h-4 w-4" />
        <AlertDescription className="text-sm">
          <div className="space-y-2">
            <p className="font-medium">Import Property Data</p>
            <p className="text-muted-foreground">
              Auto-fill property details from public records using the address you entered above.
            </p>
            <Button
              type="button"
              onClick={handleImport}
              disabled={isDisabled}
              className="mt-2 bg-primary text-primary-foreground hover:bg-primary/90"
              size="sm"
            >
              {importState === 'loading' ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Fetching property details...
                </>
              ) : importState === 'success' ? (
                <>
                  <CheckCircle className="mr-2 h-4 w-4" />
                  Data imported successfully!
                </>
              ) : (
                <>
                  <Database className="mr-2 h-4 w-4" />
                  Import Property Data
                </>
              )}
            </Button>
            {!address || !city || !state || !zipCode ? (
              <p className="text-xs text-muted-foreground mt-2">
                Complete the address fields above to enable import
              </p>
            ) : null}
          </div>
        </AlertDescription>
      </Alert>

      {importState === 'error' && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="text-sm">
            <p className="font-medium">Import Failed</p>
            <p className="mt-1">{errorMessage}</p>
            <Button
              type="button"
              variant="outline"
              onClick={handleImport}
              className="mt-2"
              size="sm"
            >
              Try Again
            </Button>
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
};
